import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, ExternalLink, FolderKanban, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Clients() {
  const { effectiveRole } = useAuth();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    package_deliverables_count: 10,
    account_manager: "",
    drive_folder_url: "",
  });

  const load = async () => {
    const { data } = await api.get("/clients");
    setClients(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/clients", form);
      toast.success("Client onboarded", {
        description: "Drive folder structure generated automatically.",
      });
      setOpen(false);
      setForm({ name: "", package_deliverables_count: 10, account_manager: "", drive_folder_url: "" });
      load();
    } catch (e) {
      toast.error("Could not create client");
    }
  };

  const remove = async (id) => {
    await api.delete(`/clients/${id}`);
    toast.success("Client archived");
    load();
  };

  return (
    <div className="p-8 lg:p-12">
      <PageHeader
        eyebrow="04 / Roster"
        title="Clients"
        subtitle="Master roster of every account on retainer. Onboarding auto-generates the standardised Drive folder tree."
        actions={
          effectiveRole === "admin" && (
            <Button
              data-testid="open-add-client-btn"
              onClick={() => setOpen(true)}
              className="rounded-full bg-white text-zinc-950 hover:bg-zinc-200"
            >
              <Plus className="h-4 w-4 mr-2" /> Add Client
            </Button>
          )
        }
      />

      <div className="rounded-xl border border-white/10 bg-zinc-900 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-b border-white/5">
              <th className="p-4 pl-6">Client</th>
              <th className="p-4">Manager</th>
              <th className="p-4">Package</th>
              <th className="p-4">Progress</th>
              <th className="p-4">Drive</th>
              <th className="p-4 pr-6"></th>
            </tr>
          </thead>
          <tbody data-testid="clients-table-body">
            {clients.length === 0 && (
              <tr>
                <td colSpan={6} className="p-10 text-center text-zinc-500 text-sm">
                  No clients yet.
                </td>
              </tr>
            )}
            {clients.map((c) => (
              <tr key={c.id} className="border-b border-white/5 last:border-0 hover:bg-zinc-900/60">
                <td className="p-4 pl-6">
                  <div className="font-medium">{c.name}</div>
                  <div className="text-[10px] font-mono text-zinc-500">{c.id}</div>
                </td>
                <td className="p-4 text-sm text-zinc-300">{c.account_manager || "—"}</td>
                <td className="p-4">
                  <span className="font-mono text-xs text-zinc-400">
                    {c.package_deliverables_count} reels/mo
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-3 min-w-[160px]">
                    <div className="h-1.5 w-24 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className="h-full bg-emerald-400"
                        style={{ width: `${c.progress_pct}%`, transition: "width 0.4s ease" }}
                      />
                    </div>
                    <span className="font-mono text-xs">
                      {c.completed_count}/{c.package_deliverables_count}
                    </span>
                  </div>
                </td>
                <td className="p-4">
                  <a
                    data-testid={`drive-link-${c.id}`}
                    href={c.drive_folder_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-white"
                  >
                    <FolderKanban className="h-3.5 w-3.5" /> Drive
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </td>
                <td className="p-4 pr-6 text-right">
                  {effectiveRole === "admin" && (
                    <button
                      data-testid={`delete-client-${c.id}`}
                      onClick={() => remove(c.id)}
                      className="h-8 w-8 rounded-md hover:bg-white/5 inline-flex items-center justify-center text-zinc-500 hover:text-rose-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-client-dialog" className="bg-zinc-950 border-white/10 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Onboard a new client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Client name</Label>
              <Input
                data-testid="client-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Brand X"
                className="mt-1.5 bg-zinc-900 border-white/10"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Deliverables / month</Label>
                <Input
                  data-testid="client-package-input"
                  type="number"
                  value={form.package_deliverables_count}
                  onChange={(e) =>
                    setForm({ ...form, package_deliverables_count: parseInt(e.target.value || "0") })
                  }
                  className="mt-1.5 bg-zinc-900 border-white/10"
                />
              </div>
              <div>
                <Label>Account manager</Label>
                <Input
                  data-testid="client-manager-input"
                  value={form.account_manager}
                  onChange={(e) => setForm({ ...form, account_manager: e.target.value })}
                  placeholder="Team lead"
                  className="mt-1.5 bg-zinc-900 border-white/10"
                />
              </div>
            </div>
            <div>
              <Label>Existing Drive folder (optional)</Label>
              <Input
                data-testid="client-drive-input"
                value={form.drive_folder_url}
                onChange={(e) => setForm({ ...form, drive_folder_url: e.target.value })}
                placeholder="Leave blank to auto-generate structure"
                className="mt-1.5 bg-zinc-900 border-white/10 font-mono text-xs"
              />
              <p className="text-[11px] text-zinc-500 mt-1.5">
                Sub-folders <span className="font-mono">01_Raw_Footage</span>,{" "}
                <span className="font-mono">02_Work_In_Progress</span> and{" "}
                <span className="font-mono">03_Final_Outputs</span> are created automatically.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              data-testid="submit-client-btn"
              onClick={submit}
              disabled={!form.name}
              className="bg-white text-zinc-950 hover:bg-zinc-200 rounded-full"
            >
              Onboard client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
